import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Kbd,
  ScrollArea,
  Tabs,
  Text,
  Tooltip,
  VisuallyHidden,
} from '@/components/ui';
import {
  IconRefresh,
  IconX,
} from '@tabler/icons-react';
import JsonView from '@uiw/react-json-view';
import { shouldShowDebugFeatures } from '@/utils/debugUtils';

interface ImportMappingDebugPanelProps {
  mappingId: string;
  mappingData: unknown;
  onRefreshData: () => void;
}

/**
 * Import Mapping Debug Panel - debug tools for import mapping details page
 * Opens/closes with 'D' key, slides in from the right
 * Shows development controls and raw API response
 */
export const ImportMappingDebugPanel: React.FC<
  ImportMappingDebugPanelProps
> = ({
  mappingId,
  mappingData,
  onRefreshData,
}) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('controls');

  const shouldShow = shouldShowDebugFeatures();

  // Keyboard handler for 'Z' key to toggle panel
  useEffect(() => {
    // Debug panels must be inert when debug features are disabled.
    if (!shouldShow) return;

    function handleKeydown(event: KeyboardEvent) {
      const isModifierActive =
        event.altKey || event.ctrlKey || event.shiftKey || event.metaKey;
      const isKeyboardInputActive = document.activeElement?.closest(`
        [contenteditable],
        [role="combobox"],
        [role="listbox"],
        [role="menu"],
        input:not([type="radio"], [type="checkbox"]),
        select,
        textarea
      `);
      const isKeyZ = event.key?.toUpperCase() === 'Z' && !isModifierActive;

      if (isKeyZ && !isKeyboardInputActive) {
        setOpen(prev => !prev);
      }
    }

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [shouldShow]);

  // Only show when debug features are enabled
  if (!shouldShow) {
    return null;
  }

  // Hidden trigger button with keyboard hint
  if (!open) {
    return (
      <VisuallyHidden>
        <Box
          position="fixed"
          bottom="4"
          right="4"
          style={{
            zIndex: 9998, // Slightly lower than DevToolsPanel
          }}
        >
          <Tooltip
            content="Press Z to show/hide Import Mapping Debug Panel"
            side="bottom"
            sideOffset={6}
          >
            <Kbd
              asChild
              size="medium"
              tabIndex={0}
              className="rt-ThemePanelShortcut"
            >
              <button
                onClick={() => setOpen(true)}
                style={{
                  background: 'var(--color-panel-solid)',
                  border: '1px solid var(--blue-6)',
                  borderRadius: '8px',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: 'var(--blue-11)',
                }}
              >
                Mapping Debug
              </button>
            </Kbd>
          </Tooltip>
        </Box>
      </VisuallyHidden>
    );
  }

  return (
    <Box
      position="fixed"
      top="0"
      right="0"
      mr="4"
      mt="4"
      style={{
        zIndex: 9998,
        overflow: 'hidden',
        maxHeight: 'calc(100vh - var(--space-4) - var(--space-4))',
        borderRadius: 'var(--radius-4)',
        backgroundColor: 'var(--color-panel-solid)',
        transformOrigin: 'top center',
        transitionProperty: 'transform, box-shadow',
        transitionDuration: '200ms',
        transitionTimingFunction: open ? 'ease-out' : 'ease-in',
        transform: open ? 'none' : 'translateX(105%)',
        boxShadow: open ? 'var(--shadow-5)' : 'var(--shadow-2)',
        border: '1px solid var(--blue-6)',
        minWidth: '600px',
        maxWidth: '90vw',
        height: 'calc(100vh - var(--space-4) - var(--space-4))',
      }}
    >
      <ScrollArea style={{ height: '100%' }}>
        <Box flexGrow="1" position="relative">
          {/* Close button */}
          <Box
            position="absolute"
            top="0"
            right="0"
            m="2"
            style={{ zIndex: 1000 }}
          >
            <Tooltip
              content="Press Z to hide Import Mapping Debug Panel"
              side="bottom"
              sideOffset={6}
            >
              <Kbd
                asChild
                size="small"
                tabIndex={0}
                className="rt-ThemePanelShortcut"
              >
                <button
                  onClick={() => setOpen(false)}
                  style={{
                    background: 'var(--color-panel-solid)',
                    border: '1px solid var(--blue-6)',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconX size={14} />
                </button>
              </Kbd>
            </Tooltip>
          </Box>

          <Box
            style={{
              marginBottom: 'var(--space-4)',
              padding: 'var(--space-5)',
            }}
          >
            <Flex align="center" justify="between">
              <Heading size="h6">Import Mapping Debug</Heading>
              <Badge color="blue" variant="soft">
                Development Only
              </Badge>
            </Flex>
            <Text size="2" color="gray">
              Press <Kbd size="small">Z</Kbd> to toggle this panel
            </Text>
          </Box>

          <Box
            style={{
              padding: '0 var(--space-5) var(--space-5) var(--space-5)',
            }}
          >
            <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
              <Tabs.List>
                <Tabs.Trigger value="controls">Development Controls</Tabs.Trigger>
                <Tabs.Trigger value="api-response">Raw API Response</Tabs.Trigger>
              </Tabs.List>

              <Tabs.Content value="controls" style={{ marginTop: '1rem' }}>
                <Card>
                  <Flex direction="column" gap="3">
                    <Text size="3" weight="bold">
                      Development Controls
                    </Text>

                    <Flex gap="2">
                      <Button
                        size="small"
                        variant="soft"
                        onClick={onRefreshData}
                        startIcon={<IconRefresh size={14} />}
                      >
                        Refresh Data
                      </Button>
                    </Flex>

                    <Text size="2" color="gray">
                      Mapping ID: {mappingId}
                    </Text>
                  </Flex>
                </Card>
              </Tabs.Content>

              <Tabs.Content value="api-response" style={{ marginTop: '1rem' }}>
                <Card>
                  <Flex direction="column" gap="3">
                    <Text size="3" weight="bold">
                      Raw API Response
                    </Text>

                    <Box
                      style={{
                        background: 'var(--gray-2)',
                        padding: '1rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        overflow: 'auto',
                        maxHeight: '600px',
                      }}
                    >
                      {mappingData &&
                      typeof mappingData === 'object' &&
                      mappingData !== null &&
                      '_apiData' in mappingData &&
                      (mappingData as { _apiData?: unknown })._apiData ? (
                        <JsonView
                          value={(mappingData as { _apiData: object })._apiData}
                          collapsed={false}
                          displayDataTypes={true}
                          style={{
                            backgroundColor: 'transparent',
                            fontSize: '13px',
                          }}
                        />
                      ) : (
                        <Text color="gray">
                          No API data available
                        </Text>
                      )}
                    </Box>
                  </Flex>
                </Card>
              </Tabs.Content>
            </Tabs.Root>
          </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
};

export default ImportMappingDebugPanel;
